import React, { useEffect, useRef, useState, useCallback } from 'react';

// ── LaundrySort — 빨래 분류 게임 ──────────────────────────────
// 떨어지는 빨래를 좌(흰색 통)/우(색깔 통)로 분류.
// 정확히 맞추면 +1, 틀리면 -1 life. 5번 틀리면 게임 오버.

const W = 360;
const H = 520;
const BIN_Y = H - 80;
const BIN_W = 120;
const BIN_H = 70;
const ITEM_R = 26;
const BEST_KEY = 'ddabak_egg_best_laundry';

// 흰 빨래 (왼쪽 통)
const WHITES = ['👕','🧦','🩳','👔'];
// 색 빨래 (오른쪽 통)
const COLORS = ['👖','🧥','👗','🩱'];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function drawEmoji(ctx, emoji, x, y, size = 38, rot = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.font = `${size}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, 0, 0);
  ctx.restore();
}

export default function LaundrySort({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(null);
  const rafRef    = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(5);
  const [combo, setCombo] = useState(0);
  const [best,  setBest]  = useState(
    () => parseInt(localStorage.getItem(BEST_KEY) || '0')
  );

  const initState = useCallback(() => ({
    items: [],
    particles: [],
    frame: 0,
    score: 0,
    lives: 5,
    combo: 0,
    comboTimer: 0,
    spawnTimer: 0,
    fallSpeed: 1.4,
  }), []);

  function spawnItem(s) {
    const isWhite = Math.random() < 0.5;
    s.items.push({
      id: Math.random(),
      x: 50 + Math.random() * (W - 100),
      y: -30,
      vx: 0,
      vy: s.fallSpeed + Math.random() * 0.5,
      rot: 0,
      vrot: (Math.random() - 0.5) * 0.04,
      isWhite,
      emoji: isWhite ? pick(WHITES) : pick(COLORS),
      grabbed: false,
      offX: 0, offY: 0,
    });
  }

  function spawnParticles(arr, x, y, color, text) {
    for (let i = 0; i < 6; i++) {
      arr.push({
        x, y,
        vx: (Math.random() - 0.5) * 4,
        vy: -1 - Math.random() * 3,
        life: 1,
        color, text,
      });
    }
  }

  const start = useCallback(() => {
    if (phase === 'idle' || phase === 'dead') {
      stateRef.current = initState();
      setScore(0); setLives(5); setCombo(0);
      setPhase('playing');
    }
  }, [phase, initState]);

  // 좌표 변환
  const toCanvasXY = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const evt = e.touches ? e.touches[0] : (e.changedTouches ? e.changedTouches[0] : e);
    return {
      x: (evt.clientX - rect.left) * (W / rect.width),
      y: (evt.clientY - rect.top)  * (H / rect.height),
    };
  };

  // 마우스/터치 — 빨래 잡고 끌어서 통에 넣기
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let dragging = null;

    const onDown = (e) => {
      e.preventDefault();
      if (phase === 'idle' || phase === 'dead') { start(); return; }
      const s = stateRef.current; if (!s) return;
      const { x, y } = toCanvasXY(e, canvas);
      // 가장 가까운 잡을 수 있는 아이템 찾기
      let best = null;
      let bestD = ITEM_R + 12;
      for (const it of s.items) {
        if (it.grabbed) continue;
        const d = Math.hypot(it.x - x, it.y - y);
        if (d < bestD) { bestD = d; best = it; }
      }
      if (best) {
        best.grabbed = true;
        best.offX = best.x - x;
        best.offY = best.y - y;
        dragging = best;
      }
    };
    const onMove = (e) => {
      if (!dragging) return;
      e.preventDefault();
      const { x, y } = toCanvasXY(e, canvas);
      dragging.x = x + dragging.offX;
      dragging.y = y + dragging.offY;
    };
    const onUp = (e) => {
      if (!dragging) return;
      const s = stateRef.current; if (!s) return;
      // 드롭 위치로 통 판정
      const dx = dragging.x;
      const dy = dragging.y;
      const inLeft  = dx < BIN_W && dy > BIN_Y - BIN_H / 2;
      const inRight = dx > W - BIN_W && dy > BIN_Y - BIN_H / 2;

      if (inLeft || inRight) {
        const wantLeft = dragging.isWhite;
        const correct = (inLeft && wantLeft) || (inRight && !wantLeft);
        if (correct) {
          s.combo++;
          const gain = 1 + Math.floor(s.combo / 3);
          s.score += gain;
          setScore(s.score);
          setCombo(s.combo);
          s.comboTimer = 90;
          spawnParticles(s.particles, dragging.x, dragging.y,
            wantLeft ? '#0ea5e9' : '#a855f7', `+${gain}`);
        } else {
          s.lives--;
          setLives(s.lives);
          s.combo = 0; setCombo(0);
          spawnParticles(s.particles, dragging.x, dragging.y, '#52525B', '✗');
          if (s.lives <= 0) {
            setPhase('dead');
          }
        }
        // 아이템 제거
        s.items = s.items.filter(i => i.id !== dragging.id);
      } else {
        // 통 밖이면 다시 떨어지게
        dragging.grabbed = false;
        dragging.vy = s.fallSpeed;
      }
      dragging = null;
    };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup',   onUp);
    canvas.addEventListener('mouseleave', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove',  onMove, { passive: false });
    canvas.addEventListener('touchend',   onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup',   onUp);
      canvas.removeEventListener('mouseleave', onUp);
      canvas.removeEventListener('touchstart', onDown);
      canvas.removeEventListener('touchmove',  onMove);
      canvas.removeEventListener('touchend',   onUp);
    };
  }, [phase, start]);

  // 게임 루프
  useEffect(() => {
    if (!stateRef.current) stateRef.current = initState();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let currentPhase = phase;

    function loop() {
      const s = stateRef.current;
      ctx.clearRect(0, 0, W, H);

      // 배경 (하늘색 청량)
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#dbeafe');
      bg.addColorStop(1, '#bfdbfe');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // 빨래줄 (상단 장식)
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 36);
      ctx.bezierCurveTo(W / 2, 60, W / 2, 60, W, 36);
      ctx.stroke();
      // 집게
      ['📌','📌','📌','📌'].forEach((p, i) => {
        ctx.font = '14px sans-serif';
        ctx.fillStyle = '#475569';
        ctx.textAlign = 'center';
        ctx.fillText('▼', 60 + i * 80, 50);
      });

      if (currentPhase === 'playing') {
        s.frame++;
        s.fallSpeed = Math.min(3.0, 1.4 + s.frame / 1200);

        // 객체 생성
        s.spawnTimer++;
        const interval = Math.max(50, 110 - Math.floor(s.frame / 360) * 8);
        if (s.spawnTimer >= interval) {
          s.spawnTimer = 0;
          spawnItem(s);
        }

        if (s.comboTimer > 0) {
          s.comboTimer--;
          if (s.comboTimer === 0) { s.combo = 0; setCombo(0); }
        }

        // 객체 이동
        for (let i = s.items.length - 1; i >= 0; i--) {
          const it = s.items[i];
          if (it.grabbed) continue;
          it.y += it.vy;
          it.vy += 0.01;
          it.rot += it.vrot;

          // 화면 밖 (놓침)
          if (it.y > H + 30) {
            s.lives--;
            setLives(s.lives);
            s.combo = 0; setCombo(0);
            spawnParticles(s.particles, it.x, H - 60, '#52525B', '놓침');
            s.items.splice(i, 1);
            if (s.lives <= 0) {
              currentPhase = 'dead';
              setPhase('dead');
            }
          }
        }

        // 파티클
        for (let i = s.particles.length - 1; i >= 0; i--) {
          const p = s.particles[i];
          p.x += p.vx; p.y += p.vy; p.vy += 0.18; p.life -= 0.025;
          if (p.life <= 0) s.particles.splice(i, 1);
        }

        if (currentPhase === 'dead') {
          setBest(prev => {
            const next = Math.max(prev, s.score);
            localStorage.setItem(BEST_KEY, String(next));
            return next;
          });
        }
      }

      // ── 빨래 통 (좌: 흰색, 우: 색깔)
      // 왼쪽 통
      ctx.fillStyle = '#0ea5e9';
      ctx.beginPath();
      ctx.roundRect(8, BIN_Y - BIN_H / 2, BIN_W - 16, BIN_H, 12);
      ctx.fill();
      ctx.fillStyle = '#0284c7';
      ctx.fillRect(8, BIN_Y - BIN_H / 2, BIN_W - 16, 8);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('흰 빨래', BIN_W / 2, BIN_Y - 10);
      ctx.font = '24px serif';
      ctx.fillText('👕', BIN_W / 2, BIN_Y + 18);

      // 오른쪽 통
      ctx.fillStyle = '#a855f7';
      ctx.beginPath();
      ctx.roundRect(W - BIN_W + 8, BIN_Y - BIN_H / 2, BIN_W - 16, BIN_H, 12);
      ctx.fill();
      ctx.fillStyle = '#7e22ce';
      ctx.fillRect(W - BIN_W + 8, BIN_Y - BIN_H / 2, BIN_W - 16, 8);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('색 빨래', W - BIN_W / 2, BIN_Y - 10);
      ctx.font = '24px serif';
      ctx.fillText('👖', W - BIN_W / 2, BIN_Y + 18);

      // 객체 그리기 (잡힌 건 약간 큼직)
      s.items.forEach(it => {
        const size = it.grabbed ? 46 : 38;
        if (it.grabbed) {
          ctx.fillStyle = 'rgba(0,0,0,0.15)';
          ctx.beginPath();
          ctx.ellipse(it.x, it.y + 16, 18, 5, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        drawEmoji(ctx, it.emoji, it.x, it.y, size, it.rot);
      });

      // 파티클
      s.particles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.globalAlpha = 1;
      });

      // UI: 점수
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.font = 'bold 30px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(String(s.score), W - 18, 92);
      ctx.fillStyle = '#075985';
      ctx.fillText(String(s.score), W - 20, 90);

      // UI: 라이프 (왼쪽)
      ctx.font = '18px serif';
      ctx.textAlign = 'left';
      for (let i = 0; i < 5; i++) {
        ctx.globalAlpha = i < s.lives ? 1 : 0.25;
        ctx.fillText('❤️', 14 + i * 22, 90);
      }
      ctx.globalAlpha = 1;

      // 콤보
      if (s.combo >= 2) {
        ctx.fillStyle = '#a855f7';
        ctx.font = 'bold 22px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`콤보 x${s.combo}`, W / 2, 92);
      }

      // 시작 화면
      if (currentPhase === 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('👕 셈셈셈!', W / 2, H / 2 - 60);
        ctx.font = 'bold 22px sans-serif';
        ctx.fillStyle = '#bfdbfe';
        ctx.fillText('다림질 끝!', W / 2, H / 2 - 28);
        ctx.font = '16px sans-serif';
        ctx.fillStyle = '#fff';
        ctx.fillText('빨래를 잡아서 통에 넣으세요', W / 2, H / 2 + 14);
        ctx.font = '13px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('👕 흰빨래 → 왼쪽   👖 색빨래 → 오른쪽', W / 2, H / 2 + 42);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '12px sans-serif';
        ctx.fillText('탭/클릭으로 시작', W / 2, H / 2 + 76);
      }

      // 게임 오버 카드
      if (currentPhase === 'dead') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 130, H / 2 - 110, 260, 220, 20);
        ctx.fill();

        ctx.fillStyle = '#075985';
        ctx.font = 'bold 26px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('마감 시간 👕', W / 2, H / 2 - 65);

        ctx.fillStyle = '#374151';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText('점수', W / 2 - 55, H / 2 - 28);
        ctx.fillText('최고', W / 2 + 55, H / 2 - 28);

        ctx.font = 'bold 36px sans-serif';
        ctx.fillStyle = '#075985';
        ctx.fillText(String(s.score), W / 2 - 55, H / 2 + 14);
        ctx.fillStyle = '#0ea5e9';
        ctx.fillText(String(Math.max(s.score, parseInt(localStorage.getItem(BEST_KEY) || '0'))), W / 2 + 55, H / 2 + 14);

        ctx.fillStyle = '#075985';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 90, H / 2 + 44, 180, 42, 12);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('다시 도전 🔄', W / 2, H / 2 + 70);
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, initState]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
      }}
    >
      <button
        onClick={e => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'rgba(255,255,255,0.2)', border: 'none',
          color: '#fff', fontSize: '22px', cursor: 'pointer',
          borderRadius: '8px', padding: '4px 10px', fontWeight: '700',
          backdropFilter: 'blur(4px)',
        }}>
        ✕
      </button>

      {best > 0 && (
        <div style={{ color: '#bfdbfe', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
          🏆 최고 기록: {best}
        </div>
      )}

      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'block',
          maxWidth: '95vw',
          maxHeight: '80vh',
          objectFit: 'contain',
          touchAction: 'none',
          cursor: 'grab',
        }}
      />

      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
        빨래를 잡아서 통에 드래그
      </p>
    </div>
  );
}
